/**
 * Modelação genérica de EXPERIÊNCIAS pedidas (módulo puro — sem IA, sem Maps,
 * sem créditos).
 *
 * Cada tipo de experiência tem requisitos, textos, duração e opções próprias.
 * Nada aqui conhece Lisboa, Peniche nem a Nacional 2: o comportamento resulta
 * do tipo, do catálogo curado e do texto do pedido.
 *
 *   food_experience  → um estabelecimento concreto (pastelaria, gelataria…)
 *   road_route       → rota rodoviária interurbana (N2, Route 66, Ruta 40…)
 *   scenic_train     → comboio panorâmico (Glacier Express, Bergensbanen…)
 *   walking_trail    → trilho/caminho a pé (Caminho de Santiago…)
 *   regional_route   → percurso regional sem meio dominante
 */

import {
  matchTemplateByName,
  type CuratedRouteTemplate,
} from "./catalog/curated-route-templates";
import {
  extractLocalSpecialties,
  type LocalSpecialty,
  type SpecialtyVenueKind,
} from "./local-specialties";
import { isCategoryExcluded, parseNegativePreferences } from "./negative-preferences";

export type ExperienceType =
  | "food_experience"
  | "road_route"
  | "scenic_train"
  | "walking_trail"
  | "regional_route";

/** Tipo da experiência a partir da categoria curada — nunca por destino. */
export function templateExperienceType(template: CuratedRouteTemplate): ExperienceType {
  switch (template.category) {
    case "scenic_train":
      return "scenic_train";
    case "scenic_road":
    case "road_trip":
      return "road_route";
    case "trail":
    case "pilgrimage":
      return "walking_trail";
    default:
      return "regional_route";
  }
}

/** A experiência atravessa várias localidades (logo, não cabe no Premium)? */
export function isMultiLocalityRoute(template: CuratedRouteTemplate): boolean {
  if ((template.approxKm ?? 0) >= 150) return true;
  if ((template.minTripDays ?? 1) >= 2) return true;
  return template.anchors.length >= 3;
}

/** Transporte adequado ao tipo de experiência (nunca "walk" numa estrada). */
export function suggestedTransportForTemplate(
  template: CuratedRouteTemplate,
): "walk" | "bike" | "car" | "transit" {
  switch (template.transport) {
    case "car":
      return "car";
    case "foot":
      return "walk";
    case "train":
    case "boat":
    case "cable_car":
      return "transit";
    default:
      return "car";
  }
}

export type NamedRouteExperience = {
  template: CuratedRouteTemplate;
  type: ExperienceType;
  multiLocality: boolean;
  suggestedTransport: "walk" | "bike" | "car" | "transit";
  /** Dias realistas para o percurso completo. */
  recommendedDays: number;
};

/** Reconhece uma rota conhecida nomeada no texto (N2, EN2, Nacional 2, …). */
export function detectNamedRouteExperience(text: string): NamedRouteExperience | null {
  const template = matchTemplateByName(text ?? "");
  if (!template) return null;
  return {
    template,
    type: templateExperienceType(template),
    multiLocality: isMultiLocalityRoute(template),
    suggestedTransport: suggestedTransportForTemplate(template),
    recommendedDays: recommendedRouteDays(template),
  };
}

/** Dias realistas do percurso completo, dentro dos limites curados. */
export function recommendedRouteDays(template: CuratedRouteTemplate): number {
  const min = Math.max(1, template.minTripDays ?? 1);
  const max = template.maxTripDays ?? Math.max(min, 14);
  const km = template.approxKm ?? 0;
  const byDistance = km > 0 ? Math.ceil(km / 250) : 0;
  const byHours = template.approxHours > 10 ? Math.ceil(template.approxHours / 8) : 1;
  return Math.min(max, Math.max(min, byDistance, byHours));
}

// ---------------------------------------------------------------------------
// Experiência gastronómica — separada de "mealPreference"
// ---------------------------------------------------------------------------

export type FoodExperienceRequest = {
  /** True → há um estabelecimento/prato concreto pedido (paragem obrigatória). */
  required: boolean;
  specialties: LocalSpecialty[];
  /** Tipos de estabelecimento a procurar, por prioridade. */
  kinds: SpecialtyVenueKind[];
};

/**
 * Uma experiência gastronómica ("pastéis de nata numa pastelaria típica") é
 * uma PARAGEM pedida, não uma refeição. Sobrevive a `mealPreference: none`,
 * mas nunca a uma negação gastronómica explícita no mesmo pedido.
 */
export function detectFoodExperience(text: string): FoodExperienceRequest {
  const specialties = extractLocalSpecialties(text ?? "");
  if (!specialties.length) return { required: false, specialties: [], kinds: [] };
  const negatives = parseNegativePreferences(text ?? "");
  // Só cai quando TODOS os tipos que serviriam a especialidade foram negados
  // no mesmo pedido (ex.: "sem restaurantes, sem cafés e sem pastelarias").
  const kept = specialties.filter((specialty) =>
    specialty.kinds.some((kind) => !isCategoryExcluded(negatives, kind)),
  );
  if (!kept.length) return { required: false, specialties: [], kinds: [] };
  const kinds: SpecialtyVenueKind[] = [];
  for (const specialty of kept) {
    for (const kind of specialty.kinds) {
      if (!kinds.includes(kind) && !isCategoryExcluded(negatives, kind)) kinds.push(kind);
    }
  }
  return { required: true, specialties: kept, kinds };

}

/**
 * O chip "Gastronomia" só pode ficar selecionado quando há refeições ou uma
 * experiência gastronómica pedida — nunca por herança de uma pesquisa antiga.
 */
export function foodInterestSelected(input: {
  mealPreference: "restaurant" | "free_break" | "none";
  foodExperience: boolean;
}): boolean {
  if (input.foodExperience) return true;
  return input.mealPreference === "restaurant";
}

// ---------------------------------------------------------------------------
// Textos e opções por TIPO — sem mensagens copiadas entre categorias
// ---------------------------------------------------------------------------

export type ExperienceModeOption = {
  id: "full" | "partial";
  label: string;
  description: string;
};

export type ExperienceModeCopy = {
  type: ExperienceType;
  title: string;
  hint: string;
  options: ExperienceModeOption[];
  /** Percurso único e sem ambiguidade: não perguntar "qual experiência?". */
  singleRoute: boolean;
};

type Strings = {
  road: { title: (n: string) => string; hint: string; full: string; fullDesc: (a: string, b: string, d: number) => string; partial: string; partialDesc: (n: string) => string };
  train: { title: (n: string) => string; hint: string; full: string; fullDesc: (a: string, b: string, h: number) => string; partial: string; partialDesc: (n: string) => string };
  trail: { title: (n: string) => string; hint: string; full: string; fullDesc: (a: string, b: string, d: number) => string; partial: string; partialDesc: (n: string) => string };
  days: (n: number) => string;
};

const STRINGS: Record<string, Strings> = {
  pt: {
    road: {
      title: (n) => `Como queres fazer a ${n}?`,
      hint: "É uma rota rodoviária única, distribuída por vários dias. Ainda não gastaste créditos.",
      full: "Percurso completo",
      fullDesc: (a, b, d) => `${a} → ${b} de ponta a ponta, distribuído por ${d} dias de condução.`,
      partial: "Apenas um troço",
      partialDesc: (n) => `Um troço da ${n}, escolhido pelos dias disponíveis e pela região de partida.`,
    },
    train: {
      title: (n) => `Como queres incluir o ${n}?`,
      hint: "A escolha define a geografia e o tempo reservado à viagem de comboio. Ainda não gastaste créditos.",
      full: "Percurso completo",
      fullDesc: (a, b, h) => `${a} → ${b} de ponta a ponta, com ≈ ${h}h reservadas para o comboio.`,
      partial: "Apenas um troço panorâmico",
      partialDesc: (n) => `Um troço do ${n} e mais tempo livre nas bases escolhidas.`,
    },
    trail: {
      title: (n) => `Como queres fazer o ${n}?`,
      hint: "É um percurso a pé por etapas. Ainda não gastaste créditos.",
      full: "Percurso completo",
      fullDesc: (a, b, d) => `${a} → ${b}, distribuído por ${d} dias de caminhada.`,
      partial: "Apenas uma etapa",
      partialDesc: (n) => `Uma etapa do ${n}, adequada aos dias disponíveis.`,
    },
    days: (n) => `${n} dias`,
  },
  en: {
    road: {
      title: (n) => `How do you want to drive the ${n}?`,
      hint: "It is a single road route spread over several days. No credits used yet.",
      full: "Full route",
      fullDesc: (a, b, d) => `${a} → ${b} end to end, spread over ${d} driving days.`,
      partial: "One section only",
      partialDesc: (n) => `One section of the ${n}, chosen by available days and starting region.`,
    },
    train: {
      title: (n) => `How do you want to include the ${n}?`,
      hint: "Your choice sets the geography and the time reserved for the train. No credits used yet.",
      full: "Full route",
      fullDesc: (a, b, h) => `${a} → ${b} end to end, with ≈ ${h}h reserved for the train.`,
      partial: "One scenic leg only",
      partialDesc: (n) => `One leg of the ${n}, with more free time at your bases.`,
    },
    trail: {
      title: (n) => `How do you want to walk the ${n}?`,
      hint: "It is a walking route in stages. No credits used yet.",
      full: "Full route",
      fullDesc: (a, b, d) => `${a} → ${b}, spread over ${d} walking days.`,
      partial: "One stage only",
      partialDesc: (n) => `One stage of the ${n}, matching the days available.`,
    },
    days: (n) => `${n} days`,
  },
};

const stringsFor = (language?: string) =>
  STRINGS[(language ?? "pt").slice(0, 2).toLowerCase()] ?? STRINGS.pt;

/** Opções de formato próprias do tipo da experiência. */
export function experienceModeCopy(
  template: CuratedRouteTemplate,
  options: { language?: string; days?: number } = {},
): ExperienceModeCopy {
  const type = templateExperienceType(template);
  const s = stringsFor(options.language);
  const first = template.anchors[0] ?? "";
  const last = template.anchors[template.anchors.length - 1] ?? "";
  const days = Math.max(options.days ?? 0, recommendedRouteDays(template));

  const pack = type === "scenic_train" ? s.train : type === "walking_trail" ? s.trail : s.road;
  const fullDesc =
    type === "scenic_train"
      ? s.train.fullDesc(first, last, template.approxHours)
      : pack.fullDesc(first, last, days);

  return {
    type,
    title: pack.title(template.name),
    hint: pack.hint,
    singleRoute: type !== "regional_route",
    options: [
      { id: "full", label: pack.full, description: fullDesc },
      { id: "partial", label: pack.partial, description: pack.partialDesc(template.name) },
    ],
  };
}

/**
 * Percurso completo automático: rota única, dias suficientes e nada no pedido
 * a indicar que só se quer um troço.
 */
export function shouldAutoSelectFullRoute(
  template: CuratedRouteTemplate,
  days: number,
  text = "",
): boolean {
  const value = (text ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/gu, "")
    .toLowerCase();
  const wantsPartial = /\b(troco|trecho|parte|etapa|segment|leg|section|solo un|apenas um)\b/u.test(value);
  if (wantsPartial) return false;
  const type = templateExperienceType(template);
  // Corredores rodoviários (N2, Route 66, Ruta 40…) exigem escolha explícita
  // entre percurso completo e troço: nunca se assume por eles.
  if (type === "regional_route" || type === "road_route") return false;
  return days >= recommendedRouteDays(template);
}
